import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, Eye, Package, AlertCircle, Loader2 } from 'lucide-react';
import { ItemsDto } from '../../../../types/items';
import { itemsService } from '../../../../services/itemsService';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { BackButton } from '../../../common/BackButton';

interface ItemsListProps {
  onItemSelect: (item: ItemsDto) => void;
  onBack?: () => void;
}

const MIN_SEARCH_LENGTH = 2;
const DEBOUNCE_MS = 300;

export const ItemsList: React.FC<ItemsListProps> = ({ onItemSelect, onBack }) => {
  const { t } = useTranslation();
  const [items, setItems] = useState<ItemsDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const searchItems = useCallback(async (term: string) => {
    if (term.trim().length < MIN_SEARCH_LENGTH) {
      setItems([]);
      setHasSearched(false);
      return;
    }

    // Cancel any in-flight request
    if (abortRef.current) {
      abortRef.current.abort();
    }
    abortRef.current = new AbortController();

    try {
      setLoading(true);
      setError(null);
      const data = await itemsService.searchCurrentItems(term.trim());
      setItems(data);
      setHasSearched(true);
    } catch (err: any) {
      if (err?.name === 'AbortError' || err?.name === 'CanceledError') return;
      setError(t('warehouse.modules.itemAdmin.errors.loadFailed'));
      console.error('Error searching items:', err);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (searchTerm.trim().length < MIN_SEARCH_LENGTH) {
      setItems([]);
      setHasSearched(false);
      setLoading(false);
      return;
    }

    debounceRef.current = setTimeout(() => {
      searchItems(searchTerm);
    }, DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [searchTerm, searchItems]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
        <div className="flex flex-col gap-4 mb-4 sm:mb-6">
          {/* Back button and title */}
          <div className="flex items-start gap-3">
            <BackButton onClick={onBack || (() => {})} />
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <Package className="h-6 w-6 sm:h-8 sm:w-8 text-blue-600 flex-shrink-0" />
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900">{t('warehouse.modules.itemAdmin.title')}</h1>
              </div>
              <p className="text-sm sm:text-base text-gray-600">{t('warehouse.modules.itemAdmin.description')}</p>
            </div>
          </div>

          {/* Stats */}
          {hasSearched && (
            <div className="flex flex-wrap items-center gap-2 text-xs sm:text-sm text-gray-500">
              <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded">
                {t('warehouse.modules.itemAdmin.stats.filtered', { '0': items.length })}
              </span>
            </div>
          )}
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
          <input
            type="text"
            placeholder={t('warehouse.modules.itemAdmin.searchPlaceholder')}
            value={searchTerm}
            onChange={handleSearch}
            autoFocus
            className="w-full pl-10 pr-4 py-3 sm:py-3 min-h-[44px] text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          {loading && (
            <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 animate-spin text-blue-500" />
          )}
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center gap-3 text-red-600">
            <AlertCircle className="h-5 w-5 flex-shrink-0" />
            <p className="text-sm">{error}</p>
          </div>
        </div>
      )}

      {/* Items Table/List */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        {!hasSearched && !loading ? (
          /* Initial state - prompt user to search */
          <div className="text-center py-16 px-4">
            <Search className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-sm sm:text-base text-gray-500 mb-1">
              {t('warehouse.modules.itemAdmin.searchPrompt')}
            </p>
            <p className="text-xs sm:text-sm text-gray-400">
              {t('warehouse.modules.itemAdmin.searchHint')}
            </p>
          </div>
        ) : items.length === 0 && hasSearched && !loading ? (
          /* No results found */
          <div className="text-center py-12 px-4">
            <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-sm sm:text-base text-gray-500">
              {t('warehouse.modules.itemAdmin.noItemsFoundSearch')}
            </p>
          </div>
        ) : items.length > 0 ? (
          <>
            {/* Mobile Card View */}
            <div className="block md:hidden divide-y divide-gray-200">
              {items.map((item) => (
                <div
                  key={`${item.companyNo}-${item.itemNo}`}
                  className="p-4 hover:bg-gray-50 active:bg-gray-100 transition-colors"
                >
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900 mb-1 break-words">
                        {item.itemNo}
                      </div>
                      <div className="text-sm text-gray-600 mb-2 break-words">
                        {item.description1 || '-'}
                      </div>
                      {item.stdLocationName && (
                        <div className="text-xs text-gray-500 mb-1">
                          📍 {item.stdLocationName}
                        </div>
                      )}
                      {item.itemBarCode && (
                        <div className="text-xs text-gray-500 font-mono">
                          🏷️ {item.itemBarCode}
                        </div>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => onItemSelect(item)}
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 min-h-[44px] rounded-lg text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800 transition-colors"
                  >
                    <Eye className="h-4 w-4" />
                    <span>{t('warehouse.modules.itemAdmin.viewDetails')}</span>
                  </button>
                </div>
              ))}
            </div>

            {/* Desktop Table View */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('warehouse.modules.itemAdmin.table.itemNumber')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('warehouse.modules.itemAdmin.table.description')}
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('warehouse.modules.itemAdmin.table.action')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {items.map((item) => (
                    <tr key={`${item.companyNo}-${item.itemNo}`} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-col">
                          <div className="text-sm font-medium text-gray-900">{item.itemNo}</div>
                          {item.stdLocationName && (
                            <div className="text-xs text-gray-500 mt-1">
                              📍 {item.stdLocationName}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">
                          {item.description1 || '-'}
                        </div>
                        {item.itemBarCode && (
                          <div className="text-xs text-gray-500 font-mono mt-1">
                            🏷️ {item.itemBarCode}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <button
                          onClick={() => onItemSelect(item)}
                          className="inline-flex items-center gap-1 px-3 py-2 min-h-[44px] rounded-md text-sm font-medium bg-blue-100 text-blue-700 hover:bg-blue-200 transition-colors"
                        >
                          <Eye className="h-4 w-4" />
                          <span>{t('warehouse.modules.itemAdmin.viewDetails')}</span>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
};
